import { GraphQL, GraphQLPagedQueryData, GraphQLQueryData } from "@rpi/openapi-api";
import moment from "moment";
import { CellDate } from "../../components/cells/CellDate";
import { CellRoute } from "../../components/cells/CellLink";
import { RpiTableColumns } from "../../rpi-core/table/RpiTable";
import { createEntityColumn } from "../../rpi-core/table/RpiTableHelpers";
import { CsvColumn } from "../../utils/csv.util";
import { formatCurrencyV2, formatPaymentAmount } from "../../utils/misc.util";
import { isNumber } from "../../utils/type.util";

type PaymentRow = NonNullable<GraphQL.PaymentsQuery["payments"]["data"]>[number];

function toTitleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function formatTransactionType(row: Pick<PaymentRow, "transactionType" | "saleParentPaymentId">): string {
  if (!row.transactionType) return "";
  const label = toTitleCase(row.transactionType);
  return row.saleParentPaymentId ? `${label} of ${row.saleParentPaymentId}` : label;
}

function formatAmount(row: Pick<PaymentRow, "amount" | "currency">): string | null {
  if (!isNumber(row.amount)) return null;
  return formatPaymentAmount(row.currency, row.amount);
}

const paymentColumn = createEntityColumn<GraphQL.PaymentResponse, GraphQL.PaymentSearchFields>();

export const PaymentColumn = {
  id: paymentColumn<"id">({
    header: "ID",
    accessor: "id",
    sortKey: GraphQL.PaymentSearchFields.Id,
    cell: ({ row }) => <CellRoute route={["payment", { id: row.id }]}>{row.id}</CellRoute>,
  }),
  customer: paymentColumn<"customerPublicId">({
    header: "Organization",
    accessor: "customerPublicId",
    sortKey: GraphQL.PaymentSearchFields.CustomerPublicId,
    cell: ({ row }) => {
      if (!row.customerPublicId) return;
      return <CellRoute route={["customer", { publicId: row.customerPublicId }]}>{row.customerPublicId}</CellRoute>;
    },
    size: 200,
  }),
  braintreeTransactionId: paymentColumn<"braintreeTransactionId">({
    header: "Braintree transaction",
    accessor: "braintreeTransactionId",
    sortKey: GraphQL.PaymentSearchFields.BraintreeTransactionId,
    size: 200,
  }),
  transactionType: paymentColumn<"transactionType" | "saleParentPaymentId">({
    header: "Type",
    accessor: "transactionType",
    sortKey: GraphQL.PaymentSearchFields.TransactionType,
    cell: ({ row }) => {
      if (row.saleParentPaymentId) {
        return (
          <CellRoute route={["payment", { id: row.saleParentPaymentId }]}>
            {`${row.transactionType} of ${row.saleParentPaymentId}`}
          </CellRoute>
        );
      }

      return row.transactionType;
    },
    size: 150,
  }),
  amount: paymentColumn<"amount" | "currency">({
    header: "Total",
    accessor: "amount",
    sortKey: GraphQL.PaymentSearchFields.Amount,
    cell: ({ row }) => isNumber(row.amount) && formatCurrencyV2(row.currency ?? undefined, row.amount),
    align: "right",
    size: 200,
  }),
  date: paymentColumn<"createdAt">({
    header: "Date",
    accessor: "createdAt",
    sortKey: GraphQL.PaymentSearchFields.CreatedAt,
    cell: ({ row }) => <CellDate date={row.createdAt} />,
    size: 200,
  }),
};

export const paymentColumns: RpiTableColumns<GraphQLPagedQueryData<GraphQL.PaymentsQuery>> = (user) => [
  { ...PaymentColumn.date, header: "Payment Date" },
  {
    ...PaymentColumn.id,
    header: "Payment ID",
    cell: ({ row }: { row: PaymentRow }) => row.id ?? null,
  },
  user.is.SUPER_ADMIN && {
    ...PaymentColumn.customer,
    cell: ({ row }: { row: PaymentRow }) => row.customerPublicId ?? null,
  },
  {
    ...PaymentColumn.transactionType,
    header: "Transaction Type",
    cell: ({ row }: { row: PaymentRow }) => formatTransactionType(row),
  },
  {
    ...PaymentColumn.amount,
    header: "Amount",
    cell: ({ row }: { row: PaymentRow }) => formatAmount(row),
  },
  user.is.SUPER_ADMIN && { ...PaymentColumn.braintreeTransactionId, header: "Braintree ID" },
];

export const customerPaymentColumns: RpiTableColumns<GraphQLPagedQueryData<GraphQL.PaymentsQuery>> = [
  PaymentColumn.id,
  PaymentColumn.date,
  PaymentColumn.braintreeTransactionId,
  PaymentColumn.transactionType,
  PaymentColumn.amount,
];

export const orderRefundColumns: RpiTableColumns<
  NonNullable<GraphQLQueryData<GraphQL.OrderQuery>["refunds"]>[number]
> = (user) => [
  PaymentColumn.id,
  PaymentColumn.date,
  user.is.SUPER_ADMIN && PaymentColumn.customer,
  user.is.SUPER_ADMIN && PaymentColumn.braintreeTransactionId,
  PaymentColumn.transactionType,
  PaymentColumn.amount,
];

export function getPaymentCsvColumns(isSuperAdmin: boolean): CsvColumn<PaymentRow>[] {
  return [
    { header: "Payment Date", value: (row) => moment(row.createdAt).format("MM/DD/YYYY") },
    { header: "Payment ID", value: (row) => String(row.id ?? "") },
    ...(isSuperAdmin ? [{ header: "Organization", value: (row: PaymentRow) => row.customerPublicId ?? "" }] : []),
    { header: "Transaction Type", value: (row) => formatTransactionType(row) },
    { header: "Amount", value: (row) => formatAmount(row) ?? "" },
    ...(isSuperAdmin ? [{ header: "Braintree ID", value: (row: PaymentRow) => row.braintreeTransactionId ?? "" }] : []),
  ];
}
